import { CurrencyPipe, DatePipe, DecimalPipe } from "@angular/common";
import {
    provideHttpClient,
    withInterceptors,
    withXhr,
} from "@angular/common/http";
import {
    inject,
    provideAppInitializer,
    provideZonelessChangeDetection,
    type ApplicationConfig,
} from "@angular/core";
import { initializeApp, provideFirebaseApp } from "@angular/fire/app";
import { getAuth, provideAuth } from "@angular/fire/auth";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideRouter, withRouterConfig } from "@angular/router";
import { provideServiceWorker } from "@angular/service-worker";
import {
    FullRouterStateSerializer,
    provideRouterStore,
    routerReducer,
} from "@ngrx/router-store";
import { provideStore } from "@ngrx/store";
import { provideStoreDevtools } from "@ngrx/store-devtools";
import {
    MissingTranslationHandler,
    provideTranslateService,
} from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import Material from "@primeuix/themes/material-compat";
import { ConfirmationService, MessageService } from "primeng/api";
import { providePrimeNG } from "primeng/config";
import { environment } from "../environments/environment";
import { ENV_CONFIG } from "../environments/environment-config";
import { provideAiApi } from "./api/ai.providers";
import { bearerTokenInterceptor } from "./api/bearer-token.interceptor";
import { globalCancelInterceptor } from "./api/http-cancel.interceptor";
import { httpErrorInterceptor } from "./api/http-error.interceptor";
import { APP_ROUTES } from "./app-routes";
import { provideAuthApi, provideAuthStore } from "./auth/auth.providers";
import { ModulesService } from "./auth/modules/modules.service";
import { provideDataSourceFilterStore } from "./core/data-source-filter.providers";
import { QRCodeService } from "./core/services/qrcode.service";
import { provideTenantApi, provideTenantStore } from "./core/tenant.providers";
import { UpdateService } from "./core/version.service";
import { provideAdminApi } from "./domains/admin/admin.providers";
import { ShowKeyHandler } from "./handler/no-keys.handler";
import { metaReducers } from "./state/app.reducer";

export const appConfig: ApplicationConfig = {
    providers: [
        provideAppInitializer(() => {
            const updateService = inject(UpdateService);
        }),
        // Increment v when the worker's CSP response headers change to reinstall it.
        provideServiceWorker("ngsw-worker.js?v=3", {
            enabled: true, // environment.production ,
            registrationStrategy: "registerImmediately",
            // registrationStrategy: 'registerWhenStable:30000'
        }),
        provideZonelessChangeDetection(),
        provideRouter(
            APP_ROUTES,
            withRouterConfig({
                onSameUrlNavigation: "reload",
            }),
        ),
        provideStore(
            {
                router: routerReducer,
            },
            {
                metaReducers,
                runtimeChecks: {
                    strictStateImmutability: false,
                    strictActionImmutability: false,
                },
            },
        ),
        provideRouterStore({
            serializer: FullRouterStateSerializer,
            stateKey: "router",
        }),
        ...provideAuthApi(),
        ...provideAiApi(),
        ...provideAdminApi(),
        ...provideAuthStore(),
        ModulesService,
        QRCodeService,
        ...provideTenantApi(),
        ...provideTenantStore(),
        ...provideDataSourceFilterStore(),
        provideStoreDevtools({
            maxAge: 150,
            logOnly: environment.production,
            trace: true,
            traceLimit: 100,
        }),
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideAuth(() => getAuth()),
        provideAnimationsAsync(),
        provideHttpClient(
            withXhr(),
            withInterceptors([
                globalCancelInterceptor,
                bearerTokenInterceptor,
                httpErrorInterceptor,
            ]),
        ),
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: "./assets/i18n/locale-",
                suffix: ".json",
            }),
            // Explicit useClass: provideMissingTranslationHandler() detects classes via
            // /^class\s/ on toString(), which fails for minified prod builds ("class{")
            // and falls back to useFactory -> "cannot be invoked without 'new'".
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: ShowKeyHandler,
            },
        }),
        providePrimeNG({
            theme: {
                preset: Material,
            },
            license: environment.primeNGLicenceKey,
        }),
        {
            provide: ENV_CONFIG,
            useValue: { environment },
        },
        DecimalPipe,
        CurrencyPipe,
        DatePipe,
        MessageService,
        ConfirmationService,
    ],
};
